import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";

import { apiClient } from "@/api/client";

/**
 * The panel screening console.
 *
 * THE TEMPLATE AND THE VERDICT BOTH COME FROM THE SERVER, and neither is
 * recomputed here. The reference design worked the recommendation out in
 * the browser; that makes each panel member's laptop an authority on a
 * hiring decision, and two people looking at the same candidate can be
 * shown different answers. This module fetches and displays. It does not
 * decide.
 */

export interface PanelItem {
  id: string;
  text: string;
  /** Stage 00 only: holds the gate shut until ticked. */
  critical?: boolean;
  /** Gates independently of any total — see the server's rules. */
  key?: boolean;
  /** Capacity stage: "conf" (1–5), "vol" (cases/day), "check" (tickbox). */
  kind?: "conf" | "vol" | "check";
}

export interface PanelStage {
  code: string;
  title: string;
  type: "gate" | "scored" | "observe" | "capacity";
  hint?: string;
  threshold?: number;
  items: PanelItem[];
}

export interface PanelTemplate {
  version: string;
  scale: { min: number; max: number };
  stages: PanelStage[];
  flags: { id: string; text: string }[];
}

export interface PanelStageResult {
  code: string;
  title: string;
  type: string;
  score: number | null;
  max_score: number | null;
  threshold: number | null;
  complete: boolean;
  met: boolean | null;
}

export type PanelOutcome =
  | "PENDING_CREDENTIALS"
  | "DO_NOT_PROGRESS"
  | "PROGRESS_TO_OFFER"
  | "HIRE_WITH_SUPERVISION"
  | "IN_PROGRESS";

export interface PanelVerdict {
  outcome: PanelOutcome;
  headline: string;
  detail: string;
  gate_cleared: boolean;
  raised_flags: string[];
  gaps: string[];
  stages: PanelStageResult[];
  capacity_total: number;
}

export interface PanelSheet {
  id: string;
  candidate_user_id: string;
  reviewer_user_id: string;
  reviewer_name: string | null;
  /** The template this sheet was SCORED AGAINST, not the current one. */
  template_version: string;
  checks: Record<string, Record<string, boolean>>;
  scores: Record<string, Record<string, number>>;
  volumes: Record<string, Record<string, number | null>>;
  flags: Record<string, boolean>;
  notes: Record<string, string>;
  submitted_at: string | null;
  updated_at: string | null;
  verdict: PanelVerdict | null;
  /** Only the caller's own sheet is writable. */
  mine: boolean;
}

/** One EARLIER screening of this candidate. Read-only, always. */
export interface PanelRound {
  test_assignment_id: string | null;
  definition_title: string | null;
  assigned_at: string | null;
  /** The sitting itself, so the round can be opened and not just listed. */
  attempt_id: string | null;
  sheets: PanelSheet[];
}

export interface PanelBoard {
  candidate_user_id: string;
  candidate_name: string | null;
  template: PanelTemplate;
  /** The round being scored. */
  test_assignment_id: string | null;
  /** THIS round's sheets only. */
  sheets: PanelSheet[];
  /**
   * Earlier rounds, newest first, read-only.
   *
   * Separate from `sheets` on purpose: a candidate's answers move
   * between screenings, so a panel forming a fresh view must not open
   * onto last year's scores. Shown alongside, never pre-filled.
   */
  history: PanelRound[];
  /** The server's answer to "may I score this", not the browser's. */
  can_edit: boolean;
  /** Who owns it, when it is not you. */
  screened_by_name: string | null;
}

/** A partial write. Stages and items MERGE server-side, so a console
 *  that saves one stage cannot erase another. */
export interface PanelSheetPatch {
  checks?: Record<string, Record<string, boolean | null>>;
  scores?: Record<string, Record<string, number | null>>;
  volumes?: Record<string, Record<string, number | null>>;
  flags?: Record<string, boolean>;
  notes?: Record<string, string>;
  submitted?: boolean;
}

export const PANEL_KEY = (candidateId: string, assignmentId?: string | null) =>
  ["panel", candidateId, assignmentId ?? ""] as const;

export function usePanelBoard(
  candidateId: string | null,
  assignmentId?: string | null,
): UseQueryResult<PanelBoard> {
  return useQuery({
    queryKey: PANEL_KEY(candidateId ?? "", assignmentId),
    enabled: !!candidateId,
    queryFn: async () => {
      const { data } = await apiClient.get<PanelBoard>(
        `/tests/panel/${candidateId}`,
        assignmentId ? { params: { assignment_id: assignmentId } } : undefined,
      );
      return data;
    },
  });
}

export function useSavePanelSheet(
  candidateId: string,
  assignmentId?: string | null,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: PanelSheetPatch) => {
      const { data } = await apiClient.put<PanelSheet>(
        `/tests/panel/${candidateId}`,
        // The round travels with every write. Without it the server
        // refuses -- a sheet with no round would edit whatever the last
        // screening of this candidate concluded.
        { ...body, assignment_id: assignmentId },
      );
      return data;
    },
    // Refetch the whole board rather than patching the cache: the
    // verdict is the server's and depends on the record as a whole, so
    // guessing at it locally is exactly the mistake this design avoids.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PANEL_KEY(candidateId, assignmentId) });
    },
  });
}
